import { useCallback, useEffect, useRef, useReducer } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import {
    CameraStartOption,
    ScannerController,
    getCameraStartOptions,
    startScannerWithFallback,
} from './cameraSelection';

export type ScannerStatus =
    | 'initializing'
    | 'ready'
    | 'scanning'
    | 'success'
    | 'error'
    | 'noCamera'
    | 'permissionDenied';

interface ScannerState {
    status: ScannerStatus;
    errorMessage: string | null;
    cameraOptions: CameraStartOption[];
}

type ScannerAction =
    | { type: 'CAMERA_READY'; cameraOptions: CameraStartOption[] }
    | { type: 'SCANNING' }
    | { type: 'SUCCESS' }
    | { type: 'ERROR'; message: string }
    | { type: 'NO_CAMERA' }
    | { type: 'PERMISSION_DENIED' }
    | { type: 'RESET' };

const INITIAL_STATE: ScannerState = {
    status: 'initializing',
    errorMessage: null,
    cameraOptions: [],
};

function scannerReducer(
    state: ScannerState,
    action: ScannerAction
): ScannerState {
    switch (action.type) {
        case 'CAMERA_READY':
            return {
                status: 'ready',
                errorMessage: null,
                cameraOptions: action.cameraOptions,
            };
        case 'SCANNING':
            return { ...state, status: 'scanning', errorMessage: null };
        case 'SUCCESS':
            return { ...state, status: 'success' };
        case 'ERROR':
            return { ...state, status: 'error', errorMessage: action.message };
        case 'NO_CAMERA':
            return {
                status: 'noCamera',
                errorMessage: null,
                cameraOptions: [],
            };
        case 'PERMISSION_DENIED':
            return {
                status: 'permissionDenied',
                errorMessage: null,
                cameraOptions: [],
            };
        case 'RESET':
            return INITIAL_STATE;
        default:
            return state;
    }
}

export const READER_ELEMENT_ID = 'imei-barcode-reader';

const SCAN_CONFIG = {
    fps: 10,
    qrbox: { width: 250, height: 150 },
    formatsToSupport: [Html5QrcodeSupportedFormats.CODE_128],
};

interface UseImeiScannerOptions {
    onSuccess: (imei: string) => void;
}

export function useImeiScanner({ onSuccess }: UseImeiScannerOptions) {
    const [state, dispatch] = useReducer(scannerReducer, INITIAL_STATE);
    const scannerRef = useRef<ScannerController | null>(null);
    const lastScanRef = useRef<string | null>(null);
    const mountedRef = useRef(true);

    // Ref avoids stale closure in the decode callback
    const onSuccessRef = useRef(onSuccess);
    onSuccessRef.current = onSuccess;

    const stopScanning = useCallback(async () => {
        const scanner = scannerRef.current;
        if (!scanner) return;
        scannerRef.current = null;
        try {
            await scanner.stop();
            scanner.clear();
        } catch (e) {
            console.warn('[ImeiScanner] stop error (safe to ignore):', e);
        }
    }, []);

    const checkCamera = useCallback(async () => {
        try {
            // getCameras() implicitly triggers permission prompt
            const devices = await Html5Qrcode.getCameras();
            if (!mountedRef.current) return;

            if (devices.length === 0) {
                dispatch({ type: 'NO_CAMERA' });
                return;
            }

            dispatch({
                type: 'CAMERA_READY',
                cameraOptions: getCameraStartOptions(devices),
            });
        } catch (err: unknown) {
            if (!mountedRef.current) return;
            const message = err instanceof Error ? err.message : String(err);
            if (
                message.includes('Permission') ||
                message.includes('NotAllowedError')
            ) {
                dispatch({ type: 'PERMISSION_DENIED' });
            } else {
                dispatch({ type: 'NO_CAMERA' });
            }
        }
    }, []);

    const startScanning = useCallback(
        async (cameraOptions: CameraStartOption[]) => {
            await stopScanning();
            if (!mountedRef.current) return;

            try {
                const scanner = await startScannerWithFallback({
                    cameraOptions,
                    scanConfig: SCAN_CONFIG,
                    createScanner: () => new Html5Qrcode(READER_ELEMENT_ID),
                    onScannerChange: (scannerInstance) => {
                        scannerRef.current = scannerInstance;
                    },
                    shouldContinue: () => mountedRef.current,
                    onDecode: (decodedText) => {
                        if (decodedText === lastScanRef.current) return;
                        lastScanRef.current = decodedText;

                        // Uncomment to restrict scanned values to 15-digit IMEI only:
                        // const IMEI_PATTERN = /^\d{15}$/;
                        // if (!IMEI_PATTERN.test(decodedText)) {
                        //     dispatch({ type: 'ERROR', message: 'Invalid IMEI format. Expected 15 digits.' });
                        //     setTimeout(() => stopScanning(), 0);
                        //     return;
                        // }

                        dispatch({ type: 'SUCCESS' });
                        onSuccessRef.current(decodedText);
                        setTimeout(() => stopScanning(), 0);
                    },
                });

                if (!mountedRef.current || !scanner) return;
                dispatch({ type: 'SCANNING' });
            } catch (err: unknown) {
                if (!mountedRef.current) return;
                dispatch({
                    type: 'ERROR',
                    message:
                        err instanceof Error
                            ? err.message
                            : 'Failed to start camera',
                });
            }
        },
        [stopScanning]
    );

    const retry = useCallback(() => {
        lastScanRef.current = null;
        dispatch({ type: 'RESET' });
    }, []);

    // Phase 1: enumerate cameras when status is 'initializing'
    useEffect(() => {
        if (state.status === 'initializing') {
            checkCamera();
        }
    }, [state.status, checkCamera]);

    // Phase 2: start camera feed once we have camera options and reader div is in DOM
    useEffect(() => {
        if (state.status === 'ready' && state.cameraOptions.length > 0) {
            startScanning(state.cameraOptions);
        }
    }, [state.status, state.cameraOptions, startScanning]);

    // Cleanup on unmount
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            stopScanning();
        };
    }, [stopScanning]);

    return {
        status: state.status,
        errorMessage: state.errorMessage,
        stopScanning,
        retry,
    };
}
